/**
 * Integration Pattern Details Component
 *
 * Displays expanded view of an integration pattern with all available information
 * organized into logical sections.
 */

import React from 'react';
import {
  BarChart3,
  Briefcase,
  GitBranch,
  Settings,
  Clock,
  Database,
  Code,
  ArrowRight,
} from 'lucide-react';

interface IntegrationPatternDetailsProps {
  pattern: {
    pattern_id: string;
    pattern_name: string;
    pattern_type: string;
    business_value?: string;
    data_flow?: string;
    trigger_event?: string;
    complexity?: string;
    frequency?: string;
    source_modules?: string[] | string;
    target_modules?: string[] | string;
    apis_used?: string[] | string;
    tables_affected?: string[] | string;
    created_at?: string;
    updated_at?: string;
  };
}

const IntegrationPatternDetails: React.FC<IntegrationPatternDetailsProps> = ({ pattern }) => {
  // Parse JSON arrays if they're strings
  const parseArray = (data: string[] | string | undefined): string[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    try {
      return JSON.parse(data);
    } catch {
      return [];
    }
  };

  const sourceModules = parseArray(pattern.source_modules);
  const targetModules = parseArray(pattern.target_modules);
  const apisUsed = parseArray(pattern.apis_used);
  const tablesAffected = parseArray(pattern.tables_affected);

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // Get frequency display
  const getFrequencyLabel = (frequency?: string) => {
    switch (frequency?.toLowerCase()) {
      case 'real_time':
        return 'Real-time';
      case 'on_demand':
        return 'On-demand';
      default:
        return frequency
          ?.split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ') || 'Unknown';
    }
  };

  // Get complexity display with color
  const getComplexityDisplay = (complexity?: string) => {
    const normalized = complexity?.toLowerCase();
    switch (normalized) {
      case 'low':
        return { label: 'Low', color: 'text-green-700 bg-green-50' };
      case 'medium':
        return { label: 'Medium', color: 'text-yellow-700 bg-yellow-50' };
      case 'high':
        return { label: 'High', color: 'text-red-700 bg-red-50' };
      default:
        return { label: complexity || 'Unknown', color: 'text-neutral-700 bg-neutral-50' };
    }
  };

  const complexityDisplay = getComplexityDisplay(pattern.complexity);

  return (
    <div className="px-6 pb-6 space-y-6">
      {/* Overview Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-5 h-5 text-primary-600" />
          <h5 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide">
            Overview
          </h5>
        </div>
        <div className="bg-neutral-50 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide block mb-1">
                Frequency
              </span>
              <span className="text-sm text-neutral-900 font-medium">
                {getFrequencyLabel(pattern.frequency)}
              </span>
            </div>
            <div>
              <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide block mb-1">
                Complexity
              </span>
              <span
                className={`inline-block text-sm font-medium px-2 py-0.5 rounded ${complexityDisplay.color}`}
              >
                {complexityDisplay.label}
              </span>
            </div>
          </div>
          {pattern.trigger_event && (
            <div className="pt-3 border-t border-neutral-200">
              <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide block mb-1">
                Trigger Event
              </span>
              <span className="text-sm text-neutral-900">
                {pattern.trigger_event}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Business Value Section */}
      {pattern.business_value && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Briefcase className="w-5 h-5 text-primary-600" />
            <h5 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide">
              Business Value
            </h5>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-sm text-neutral-700 leading-relaxed">{pattern.business_value}</p>
          </div>
        </div>
      )}

      {/* Data Flow Section */}
      {pattern.data_flow && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <GitBranch className="w-5 h-5 text-primary-600" />
            <h5 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide">
              Data Flow
            </h5>
          </div>
          <div className="bg-purple-50 border border-purple-100 rounded-lg p-5">
            <p className="text-sm text-neutral-700 leading-relaxed mb-5">{pattern.data_flow}</p>

            {/* Visual Flow Diagram */}
            {sourceModules.length > 0 || targetModules.length > 0 ? (
              <div className="flex flex-wrap items-center justify-center gap-3 p-5 bg-white rounded-lg border border-purple-200">
                {/* Source Modules */}
                {sourceModules.length > 0 && (
                  <div className="text-center">
                    <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm mb-1.5">
                      {sourceModules.join(', ')}
                    </div>
                    <p className="text-xs text-neutral-500 font-medium uppercase tracking-wide">
                      Source
                    </p>
                  </div>
                )}

                {/* Arrow */}
                {sourceModules.length > 0 && targetModules.length > 0 && (
                  <ArrowRight className="w-5 h-5 text-purple-400 flex-shrink-0" />
                )}

                {/* APIs (if any) */}
                {apisUsed.length > 0 && (
                  <>
                    <div className="text-center">
                      <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm mb-1.5">
                        API
                      </div>
                      <p className="text-xs text-neutral-500 font-medium max-w-[120px] truncate">
                        {apisUsed.join(', ')}
                      </p>
                    </div>
                    {targetModules.length > 0 && (
                      <ArrowRight className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    )}
                  </>
                )}

                {/* Target Modules */}
                {targetModules.length > 0 && (
                  <div className="text-center">
                    <div className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-lg font-semibold text-sm mb-1.5">
                      {targetModules.join(', ')}
                    </div>
                    <p className="text-xs text-neutral-500 font-medium uppercase tracking-wide">
                      Target
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 bg-white rounded-lg border border-purple-200 text-center">
                <p className="text-sm text-neutral-500">No module flow information available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Technical Details Section */}
      {(sourceModules.length > 0 ||
        targetModules.length > 0 ||
        apisUsed.length > 0 ||
        tablesAffected.length > 0) && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Settings className="w-5 h-5 text-primary-600" />
            <h5 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide">
              Technical Details
            </h5>
          </div>
          <div className="bg-neutral-50 rounded-lg p-5 space-y-4">
            {/* Source Modules */}
            {sourceModules.length > 0 && (
              <div>
                <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide block mb-2">
                  Source Modules
                </span>
                <div className="flex flex-wrap gap-2">
                  {sourceModules.map((module, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 bg-purple-100 text-purple-700 text-sm font-semibold rounded-lg"
                    >
                      {module}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Target Modules */}
            {targetModules.length > 0 && (
              <div>
                <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide block mb-2">
                  Target Modules
                </span>
                <div className="flex flex-wrap gap-2">
                  {targetModules.map((module, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 bg-green-100 text-green-700 text-sm font-semibold rounded-lg"
                    >
                      {module}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* APIs Used */}
            {apisUsed.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Code className="w-4 h-4 text-neutral-500" />
                  <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
                    APIs Used
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {apisUsed.map((api, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-lg"
                    >
                      {api}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Tables Affected */}
            {tablesAffected.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-4 h-4 text-neutral-500" />
                  <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
                    Tables Affected
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {tablesAffected.map((table, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 bg-neutral-200 text-neutral-700 text-sm font-semibold rounded-lg font-mono"
                    >
                      {table}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Metadata Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-5 h-5 text-primary-600" />
          <h5 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide">
            Metadata
          </h5>
        </div>
        <div className="bg-neutral-50 rounded-lg p-4 space-y-2 text-sm">
          <div className="flex items-start justify-between">
            <span className="font-medium text-neutral-700">Created:</span>
            <span className="text-neutral-600">{formatDate(pattern.created_at)}</span>
          </div>
          <div className="flex items-start justify-between">
            <span className="font-medium text-neutral-700">Updated:</span>
            <span className="text-neutral-600">{formatDate(pattern.updated_at)}</span>
          </div>
          <div className="flex items-start justify-between">
            <span className="font-medium text-neutral-700">Pattern ID:</span>
            <span className="text-neutral-600 font-mono text-xs">{pattern.pattern_id}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationPatternDetails;
